import {
  erc20Abi,
  maxUint256,
  type Address,
  type Hash,
  type WalletClient,
} from "viem";

import { ERC4626_ABI, SUPPLY_VAULT } from "@/config/vault";
import { USDC } from "@/config/tokens";

/** Grant the vault an unbounded USDC allowance so `deposit` can pull funds. */
export const approveVaultUsdc = async (
  wallet: WalletClient,
  args: { readonly account: Address },
): Promise<Hash> => {
  return await wallet.writeContract({
    account: args.account,
    chain: null,
    address: USDC.address,
    abi: erc20Abi,
    functionName: "approve",
    args: [SUPPLY_VAULT.address, maxUint256],
  });
};

/** Supply `assetsRaw` USDC into the vault, minting shares to the user. */
export const depositToVault = async (
  wallet: WalletClient,
  args: { readonly account: Address; readonly assetsRaw: bigint },
): Promise<Hash> => {
  return await wallet.writeContract({
    account: args.account,
    chain: null,
    address: SUPPLY_VAULT.address,
    abi: ERC4626_ABI,
    functionName: "deposit",
    args: [args.assetsRaw, args.account],
  });
};

/** Withdraw `assetsRaw` USDC from the vault, burning the user's shares. */
export const withdrawFromVault = async (
  wallet: WalletClient,
  args: { readonly account: Address; readonly assetsRaw: bigint },
): Promise<Hash> => {
  return await wallet.writeContract({
    account: args.account,
    chain: null,
    address: SUPPLY_VAULT.address,
    abi: ERC4626_ABI,
    functionName: "withdraw",
    args: [args.assetsRaw, args.account, args.account],
  });
};

/**
 * Redeem `sharesRaw` vault shares back to USDC. Preferred over `withdraw` for a
 * full exit: redeeming the exact share balance leaves no dust and sidesteps the
 * asset↔share rounding that an assets-denominated `withdraw` of the whole
 * position can trip on. This mirrors what the Morpho app does on "Withdraw all".
 */
export const redeemFromVault = async (
  wallet: WalletClient,
  args: { readonly account: Address; readonly sharesRaw: bigint },
): Promise<Hash> => {
  return await wallet.writeContract({
    account: args.account,
    chain: null,
    address: SUPPLY_VAULT.address,
    abi: ERC4626_ABI,
    functionName: "redeem",
    args: [args.sharesRaw, args.account, args.account],
  });
};
